<template>
  <div v-if="meterDevice" class="vehicle-charges">
    <!-- En tête : le mois, puis la source des recharges -->
    <div class="d-flex align-center flex-wrap mb-3" style="gap: 8px">
      <v-btn icon small @click="shift(-1)"><v-icon>mdi-chevron-left</v-icon></v-btn>
      <div class="text-h6 text-capitalize vehicle-charges__month">{{ monthLabel }}</div>
      <v-btn icon small :disabled="isCurrentMonth" @click="shift(1)"><v-icon>mdi-chevron-right</v-icon></v-btn>
      <v-spacer />
      <!-- Le fournisseur dont les recharges font foi, en lecture : il se choisit sur l'onglet Connexion du véhicule,
           là où l'on voit ses connexions (un sélecteur ici ne disait pas qu'il changeait ce qui crée les CDR) -->
      <v-tooltip v-if="provider" bottom>
        <template #activator="{ on, attrs }">
          <span class="caption grey--text" v-bind="attrs" v-on="on">{{ $t('meter-device.vehicle-charges.provider.source', {provider: providerName(provider)}) }}</span>
        </template>
        <span>{{ $t('meter-device.vehicle-charges.provider.where-to-change') }}</span>
      </v-tooltip>
      <v-btn small text :loading="loading" @click="load"><v-icon left small>mdi-refresh</v-icon>{{ $t('meter-device.vehicle-charges.refresh') }}</v-btn>
    </div>

    <!-- Les trois lieux, d'un coup d'œil : le domicile est ce qui est remboursé -->
    <v-card outlined class="mb-4">
      <div class="d-flex flex-wrap">
        <div v-for="p in places" :key="p" class="vehicle-charges__total">
          <div class="d-flex align-center mb-1">
            <v-icon small :color="placeColor(p)" class="mr-1">{{ placeIcon(p) }}</v-icon>
            <span class="overline">{{ $t('meter-device.vehicle-charges.place.' + p) }}</span>
          </div>
          <div class="text-h6 font-weight-medium">{{ number(totals[p] ? totals[p].energyKwh : 0) }} <span class="body-2 grey--text">kWh</span></div>
          <div class="caption grey--text">
            {{ $tc('meter-device.vehicle-charges.count', totals[p] ? totals[p].count : 0) }}<span v-if="p === 'home'"> · {{ $t('meter-device.vehicle-charges.refundable') }}</span>
          </div>
        </div>
      </div>
    </v-card>

    <v-data-table
      :headers="headers"
      :items="items"
      :loading="loading"
      :items-per-page="50"
      :footer-props="{'items-per-page-options': [50, 100, -1]}"
      :no-data-text="$t('meter-device.vehicle-charges.empty')"
      class="elevation-0 vehicle-charges__table"
    >
      <template #item.startTime="{ item }">
        <div class="text-capitalize">{{ item.startTime | formatDate('ddd DD/MM') }}</div>
        <div class="caption grey--text">{{ item.startTime | formatDate('HH[h]mm') }}</div>
      </template>
      <template #item.stopTime="{ item }">
        <template v-if="item.stopTime">
          <div class="text-capitalize">{{ item.stopTime | formatDate('ddd DD/MM') }}</div>
          <div class="caption grey--text">{{ item.stopTime | formatDate('HH[h]mm') }}</div>
        </template>
        <span v-else class="primary--text">{{ $t('meter-device.vehicle-charges.running') }}</span>
      </template>
      <template #item.duration="{ item }">
        <span v-if="item.running" class="primary--text">{{ $t('meter-device.vehicle-charges.running') }}</span>
        <span v-else>{{ duration(item) }}</span>
      </template>
      <template #item.place="{ item }">
        <div class="d-flex align-center">
          <v-chip x-small label :color="placeColor(item.place)" text-color="white" class="mr-2 flex-shrink-0">
            <v-icon x-small left>{{ placeIcon(item.place) }}</v-icon>{{ $t('meter-device.vehicle-charges.place.' + item.place) }}
          </v-chip>
          <span class="text-truncate vehicle-charges__label" :title="item.placeLabel">{{ item.placeLabel }}</span>
          <v-chip v-if="item.roaming" x-small outlined class="ml-1 flex-shrink-0">{{ $t('meter-device.vehicle-charges.roaming') }}</v-chip>
        </div>
      </template>
      <template #item.battery="{ item }">
        <span v-if="item.batteryStart != null || item.batteryStop != null" class="text-no-wrap">
          {{ percent(item.batteryStart) }}<v-icon x-small class="mx-1">mdi-arrow-right</v-icon>{{ percent(item.batteryStop) }}
        </span>
        <span v-else class="grey--text">–</span>
      </template>
      <template #item.rangeAddedKm="{ item }">
        <span v-if="item.rangeAddedKm != null" class="text-no-wrap">+{{ number(item.rangeAddedKm, 0) }} km</span>
        <span v-else class="grey--text">–</span>
      </template>
      <template #item.odometerKm="{ item }">
        <span v-if="item.odometerKm != null" class="text-no-wrap">{{ number(item.odometerKm, 0) }} km</span>
        <span v-else class="grey--text">–</span>
      </template>
      <template #item.energyKwh="{ item }">
        <span class="font-weight-medium text-no-wrap">{{ item.energyKwh != null ? number(item.energyKwh) : '–' }} <span class="grey--text">kWh</span></span>
      </template>
      <template #item.cdr="{ item }">
        <v-tooltip bottom :disabled="item.cdr !== 'pending'">
          <template #activator="{ on, attrs }">
            <!-- Créé : ouvre la fiche du CDR -->
            <v-chip
              x-small label outlined
              :color="item.cdr === 'created' ? 'green' : 'grey'"
              :to="item.cdrId ? {name: 'cdr-detail', params: {id: item.cdrId}} : null"
              v-bind="attrs" v-on="on"
            >
              {{ $t('meter-device.vehicle-charges.cdr.' + item.cdr) }}
              <v-icon v-if="item.cdrId" x-small right>mdi-open-in-new</v-icon>
            </v-chip>
          </template>
          <span>{{ $t('meter-device.vehicle-charges.cdr.pending-hint') }}</span>
        </v-tooltip>
        <!-- Le véhicule avait donné un CDR, retiré quand la borne a mesuré la recharge : on ne change pas un chiffre
             sans le dire -->
        <div v-if="item.refinedByChargingPoint" class="caption grey--text text-no-wrap">
          <v-icon x-small>mdi-tune-variant</v-icon> {{ $t('meter-device.vehicle-charges.cdr.refined') }}
        </div>
        <v-tooltip v-if="item.coverConflict" bottom>
          <template #activator="{ on, attrs }">
            <v-icon small color="orange" v-bind="attrs" v-on="on">mdi-alert</v-icon>
          </template>
          <span>{{ $t('meter-device.vehicle-charges.cdr.cover-conflict') }}</span>
        </v-tooltip>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import dayjs from "dayjs";
import MeterDeviceRepository from "@repository/MeterDeviceRepository";

export default {
  data() {
    return {
      month: dayjs().format('YYYY-MM'),
      places: ['home', 'organization', 'elsewhere'],
      totals: {},
      items: [],
      loading: false,
      provider: null,
    }
  },

  computed: {
    meterDevice() {
      return this.$parent.meterDevice
    },
    isCurrentMonth() {
      return this.month >= dayjs().format('YYYY-MM')
    },
    monthLabel() {
      return dayjs(this.month + '-01').locale(this.$i18n.locale).format('MMMM YYYY')
    },
    headers() {
      return [
        {text: this.$t('meter-device.vehicle-charges.start'), value: 'startTime', width: 110},
        {text: this.$t('meter-device.vehicle-charges.end'), value: 'stopTime', width: 110},
        {text: this.$t('meter-device.vehicle-charges.duration'), value: 'duration', sortable: false, width: 90},
        {text: this.$t('meter-device.vehicle-charges.battery'), value: 'battery', sortable: false, width: 120},
        {text: this.$t('meter-device.vehicle-charges.range-added'), value: 'rangeAddedKm', align: 'right', width: 110},
        {text: this.$t('meter-device.vehicle-charges.odometer'), value: 'odometerKm', align: 'right', width: 110},
        {text: this.$t('meter-device.vehicle-charges.energy'), value: 'energyKwh', align: 'right', width: 110},
        {text: this.$t('meter-device.vehicle-charges.where'), value: 'place'},
        {text: this.$t('meter-device.vehicle-charges.cdr.title'), value: 'cdr', sortable: false, width: 110},
      ]
    },
  },

  watch: {
    'meterDevice.id': {
      immediate: true,
      handler(id) {
        if (id) this.load()
      },
    },
  },

  methods: {
    load() {
      this.loading = true
      MeterDeviceRepository.getVehicleMeterCharges(this.meterDevice.id, this.month).then(result => {
        this.totals = result.totals
        this.items = result.items
        this.provider = result.provider
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.loading = false
      })
    },
    providerName(provider) {
      return {SMARTCAR: 'Smartcar', GEOTAB: 'Geotab', LINK_BY_CAR: 'Link by Car', VOLTERAS: 'Volteras', ENODE: 'Enode'}[provider] || provider
    },
    shift(months) {
      this.month = dayjs(this.month + '-01').add(months, 'month').format('YYYY-MM')
      this.load()
    },
    duration(item) {
      const minutes = dayjs(item.stopTime).diff(dayjs(item.startTime), 'minute')
      return Math.floor(minutes / 60) + 'h' + String(minutes % 60).padStart(2, '0')
    },
    number(value, digits = 2) {
      return new Intl.NumberFormat(this.$i18n.locale, {maximumFractionDigits: digits}).format(value || 0)
    },
    percent(value) {
      return value != null ? Math.round(value) + ' %' : '?'
    },
    placeColor(place) {
      return {home: 'green', organization: 'blue', elsewhere: 'grey'}[place]
    },
    placeIcon(place) {
      return {home: 'mdi-home', organization: 'mdi-office-building', elsewhere: 'mdi-map-marker'}[place]
    },
  },
}
</script>

<style lang="scss">
.vehicle-charges__month {
  min-width: 170px;
  text-align: center;
}
.vehicle-charges__total {
  flex: 1 1 180px;
  padding: 12px 16px;
}
.vehicle-charges__total + .vehicle-charges__total {
  border-left: 1px solid rgba(0, 0, 0, 0.12);
}
.vehicle-charges__label {
  max-width: 220px;
}
.vehicle-charges__table td {
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}
</style>
